import React from 'react';
import { Phone, MapPin, Package, CheckCircle2, Clock, Send, Plus, Truck, Building, GripVertical, User } from 'lucide-react';
import { DELIVERY_STATUS } from '../../services/deliveryService';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const STATUS_META = {
  [DELIVERY_STATUS.PENDING]: { label: 'Adres Bekleniyor', cls: 'bg-navy-700 text-navy-200', Icon: Clock },
  [DELIVERY_STATUS.ADDRESS_REQUESTED]: { label: 'Adres İstendi', cls: 'bg-amber-500/20 text-amber-300 border border-amber-500/30', Icon: Send },
  [DELIVERY_STATUS.ADDRESS_RECEIVED]: { label: 'Adres Alındı', cls: 'bg-blue-500/20 text-blue-300 border border-blue-500/30', Icon: MapPin },
  [DELIVERY_STATUS.DELIVERED]: { label: 'Teslim Edildi', cls: 'bg-forest-500/20 text-forest-300 border border-forest-500/30', Icon: CheckCircle2 },
  [DELIVERY_STATUS.CENTER_PICKUP]: { label: 'Vakıftan Alınacak', cls: 'bg-purple-500/20 text-purple-300 border border-purple-500/30', Icon: Building },
  [DELIVERY_STATUS.SELF_PICKUP]: { label: 'Kendi Alacak', cls: 'bg-orange-500/20 text-orange-300 border border-orange-500/30', Icon: User },
};

export default function DeliveryCard({
  mode = 'delivery',
  data,
  selected = false,
  onSelectChange,
  onClick,
  onAdd,
  onCenterDelivery,
  onSelfPickup,
  branchLabel,
}) {
  const isShareholder = mode === 'shareholder';
  const status = data.status || DELIVERY_STATUS.PENDING;
  const meta = STATUS_META[status] || STATUS_META[DELIVERY_STATUS.PENDING];
  const Icon = meta.Icon;

  const hasAddress = !!(data.address && data.address.trim());
  const isDelivered = status === DELIVERY_STATUS.DELIVERED;
  const checkboxEnabled = !isShareholder && hasAddress && !isDelivered;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: data.id, disabled: isShareholder });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative bg-navy-800 p-4 rounded-xl border transition-all ${
        selected ? 'border-forest-500 shadow-lg shadow-forest-500/10' : 'border-navy-700/50 hover:border-navy-600'
      } ${isDelivered ? 'opacity-70' : ''} ${isDragging ? 'opacity-50 z-10 shadow-2xl border-forest-500' : ''}`}
    >
      {/* Mobil ve desktop uyumlu sürükleme kulpu (sadece liste sekmesinde gösterilir) */}
      {!isShareholder && (
        <div
          {...attributes}
          {...listeners}
          className="absolute left-0 top-0 bottom-0 w-8 flex items-center justify-center cursor-grab active:cursor-grabbing text-navy-500 hover:text-white rounded-l-xl hover:bg-navy-700/50 transition-colors"
        >
          <GripVertical className="w-5 h-5" />
        </div>
      )}

      {checkboxEnabled && (
        <label className="absolute top-3 right-3 inline-flex items-center cursor-pointer" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={selected}
            onChange={(e) => onSelectChange?.(e.target.checked)}
            className="w-4 h-4 accent-forest-500"
          />
        </label>
      )}

      <button
        type="button"
        onClick={isShareholder ? undefined : onClick}
        className={`w-full text-left ${!isShareholder ? 'pl-6' : ''} ${isShareholder ? 'cursor-default' : 'cursor-pointer'}`}
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-navy-900 border border-navy-700 flex items-center justify-center shrink-0">
            <Truck className="w-5 h-5 text-forest-400" />
          </div>
          <div className="flex-1 min-w-0 pr-6">
            <div className="text-white text-sm font-bold truncate">{data.customerName || '-'}</div>
            <div className="text-xs text-navy-400 mt-0.5 flex items-center gap-1">
              <Phone className="w-3 h-3" />
              <span className="font-mono">{data.customerPhone || '-'}</span>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 mt-3">
          {branchLabel && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-navy-900 text-navy-300 border border-navy-700 uppercase tracking-wide">
              {branchLabel}
            </span>
          )}
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-navy-900 text-navy-300 border border-navy-700 inline-flex items-center gap-1">
            <Package className="w-3 h-3" />
            {Number(data.shareCount) || 0} hisse
          </span>
          {!isShareholder && (
            <span className={`text-[10px] px-2 py-0.5 rounded-full inline-flex items-center gap-1 font-medium ${meta.cls}`}>
              <Icon className="w-3 h-3" />
              {meta.label}
            </span>
          )}
        </div>

        {!isShareholder && hasAddress && (
          <div className="mt-3 p-2 bg-navy-900/60 border border-navy-700/50 rounded-lg flex items-start gap-2">
            <MapPin className="w-3.5 h-3.5 text-blue-400 mt-0.5 shrink-0" />
            <span className="text-xs text-navy-200 leading-relaxed line-clamp-3">{data.address}</span>
          </div>
        )}
      </button>

      {isShareholder && (
        <div className="flex gap-1.5 mt-3">
          <button
            type="button"
            onClick={onAdd}
            disabled={data.alreadyAdded}
            className="flex-1 py-1.5 bg-forest-600/20 text-forest-300 border border-forest-500/30 hover:bg-forest-500 hover:text-white rounded-lg text-[10px] font-bold flex flex-col items-center justify-center gap-1 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-3.5 h-3.5" />
            {data.alreadyAdded ? 'Listede' : 'Eve Teslim'}
          </button>
          <button
            type="button"
            onClick={onCenterDelivery}
            disabled={data.alreadyAdded}
            className="flex-1 py-1.5 bg-purple-600/20 text-purple-300 border border-purple-500/30 hover:bg-purple-500 hover:text-white rounded-lg text-[10px] font-bold flex flex-col items-center justify-center gap-1 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Building className="w-3.5 h-3.5" />
            Vakıf Teslim
          </button>
          <button
            type="button"
            onClick={onSelfPickup}
            disabled={data.alreadyAdded}
            className="flex-1 py-1.5 bg-orange-600/20 text-orange-300 border border-orange-500/30 hover:bg-orange-500 hover:text-white rounded-lg text-[10px] font-bold flex flex-col items-center justify-center gap-1 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <User className="w-3.5 h-3.5" />
            Kendi Alacak
          </button>
        </div>
      )}
    </div>
  );
}
